import Reveal from "../components/Reveal";

const LAST_UPDATED = "23 April 2026";
const COMPANY = "Silly Butterfly";
const EMAIL = "hello@sillybutterfly.com";

const H2 = {
  fontFamily: "var(--font-display)",
  fontSize: "1.2rem",
  fontWeight: 600,
  color: "var(--plum)",
  margin: "0 0 12px",
};

const H3 = {
  fontFamily: "var(--font-body)",
  fontSize: "0.95rem",
  fontWeight: 700,
  color: "var(--plum)",
  margin: "16px 0 8px",
};

const P = {
  color: "var(--text-secondary)",
  lineHeight: 1.8,
  fontSize: "0.95rem",
  margin: "0 0 14px",
};

const LI = {
  color: "var(--text-secondary)",
  lineHeight: 1.8,
  fontSize: "0.95rem",
  marginBottom: "6px",
  paddingLeft: "4px",
};

const PROSE_SECTION = {
  paddingBottom: "32px",
  marginBottom: "32px",
  borderBottom: "1px solid rgba(212,168,83,0.15)",
};

const PROSE_SECTION_LAST = {
  paddingBottom: 0,
  marginBottom: 0,
};

// Pill label for cookie type classification
const TAG = (bg) => ({
  display: "inline-block",
  fontSize: "0.72rem",
  fontWeight: 600,
  fontFamily: "var(--font-body)",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  padding: "2px 8px",
  borderRadius: "99px",
  marginLeft: "8px",
  verticalAlign: "middle",
  background: bg,
  color: "var(--plum)",
});

export default function Cookies() {
  return (
    <main style={{ paddingTop: "var(--nav-h)" }}>
      {/* Content */}
      <section className="sb-section">
        <div className="sb-container" style={{ maxWidth: "980px" }}>
          <Reveal>
            <div style={{
              background: "var(--white)", borderRadius: "var(--radius-lg)",
              padding: "clamp(32px, 5vw, 56px)",
              boxShadow: "var(--shadow-md)",
              border: "1px solid rgba(212,168,83,0.08)",
            }}>

              <div style={PROSE_SECTION}>
                <span style={{ display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "0.78rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--gold)", marginBottom: "10px" }}>
                  Legal
                </span>
                <h1 style={{ color: "var(--plum)", margin: "0 0 10px" }}>
                  Cookies Policy
                </h1>
                <p style={{ ...P, marginBottom: 0, fontSize: "0.85rem" }}>
                  Last updated: {LAST_UPDATED}
                </p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>1. What Are Cookies?</h2>
                <p style={P}>Cookies are small text files that are placed on your device when you visit a website. They are widely used to make websites work more efficiently, remember your preferences, and provide information to site owners.</p>
                <p style={P}>This policy explains how {COMPANY} uses cookies on our website and how you can manage them.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>2. How We Use Cookies</h2>
                <p style={P}>We use cookies to:</p>
                <ul style={{ paddingLeft: "20px", margin: "0 0 14px" }}>
                  <li style={LI}>Ensure our website functions correctly and loads efficiently</li>
                  <li style={LI}>Understand how visitors use our site so we can improve it</li>
                  <li style={LI}>Remember your preferences during your visit</li>
                  <li style={LI}>Measure the effectiveness of our marketing (where applicable)</li>
                </ul>
                <p style={P}>We do not use cookies to collect sensitive personal information or to show you targeted advertising without your consent.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>3. Types of Cookies We Use</h2>

                <h3 style={H3}>
                  Strictly Necessary
                  <span style={TAG("rgba(212,168,83,0.18)")}>Always Active</span>
                </h3>
                <p style={P}>These cookies are essential for the website to function. They cannot be disabled. They are usually set in response to actions you take, such as submitting a form or navigating between pages.</p>
                <p style={{ ...P, marginBottom: 0 }}>Examples include session management and security tokens.</p>

                <h3 style={H3}>
                  Functional
                  <span style={TAG("rgba(199,125,138,0.15)")}>Optional</span>
                </h3>
                <p style={P}>Functional cookies allow the website to remember choices you make (such as your location or language) and provide enhanced, more personalised features.</p>

                <h3 style={H3}>
                  Analytics
                  <span style={TAG("rgba(199,125,138,0.15)")}>Optional</span>
                </h3>
                <p style={P}>Analytics cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This allows us to improve the user experience over time.</p>
                <p style={P}>We may use tools such as Google Analytics. Data collected is aggregated and does not identify individual users. You can opt out of Google Analytics by installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)" }}>Google Analytics Opt-out Browser Add-on</a>.</p>

                <h3 style={H3}>
                  Marketing
                  <span style={TAG("rgba(199,125,138,0.15)")}>Optional</span>
                </h3>
                <p style={P}>Marketing cookies may be set by us or third-party services to build a profile of your interests and show relevant content. We currently use these only in a limited capacity and with your consent where required.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>4. Third-Party Cookies</h2>
                <p style={P}>Some cookies on our site are set by third-party services. These include:</p>
                <ul style={{ paddingLeft: "20px", margin: "0 0 14px" }}>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Netlify</strong> — our hosting platform may set cookies related to site performance and form functionality.</li>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Google Analytics</strong> — anonymous usage analytics (if enabled).</li>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>WhatsApp</strong> — if you click our WhatsApp widget, WhatsApp's own cookie and privacy policy will apply.</li>
                </ul>
                <p style={P}>We are not responsible for the content or privacy practices of third-party sites. We encourage you to read their privacy policies independently.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>5. Managing Cookies</h2>
                <p style={P}>You can control and manage cookies in several ways:</p>
                <p style={{ ...P, marginBottom: "8px" }}><strong style={{ color: "var(--plum)" }}>Browser settings:</strong></p>
                <p style={P}>Most browsers allow you to refuse or delete cookies. The process differs by browser — please refer to your browser's help documentation:</p>
                <ul style={{ paddingLeft: "20px", margin: "0 0 14px" }}>
                  <li style={LI}><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)" }}>Google Chrome</a></li>
                  <li style={LI}><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)" }}>Mozilla Firefox</a></li>
                  <li style={LI}><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)" }}>Safari</a></li>
                  <li style={LI}><a href="https://support.microsoft.com/en-us/windows/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)" }}>Microsoft Edge</a></li>
                </ul>
                <p style={P}>Please note that disabling cookies may affect the functionality of this and other websites you visit.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>6. Changes to This Policy</h2>
                <p style={P}>We may update this Cookies Policy from time to time to reflect changes in technology, legislation, or our practices. Any updates will be posted on this page with a revised "last updated" date.</p>
              </div>

              <div style={PROSE_SECTION_LAST}>
                <h2 style={H2}>7. Contact Us</h2>
                <p style={{ ...P, marginBottom: "6px" }}>If you have any questions about our use of cookies, please contact us:</p>
                <p style={P}>
                  <strong style={{ color: "var(--plum)" }}>{COMPANY}</strong><br />
                  <a href={`mailto:${EMAIL}`} style={{ color: "var(--gold)" }}>{EMAIL}</a>
                </p>
              </div>

            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
